function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          'form',
          `${element.form.getAttribute('id')}-${uniqueKey}`
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute(
    'aria-expanded',
    summary.parentNode.hasAttribute('open')
  );

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute(
      'aria-expanded',
      !event.currentTarget.closest('details').hasAttribute('open')
    );
  });

  if (summary.closest('menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
      '*'
    );
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
    this.errorWrapper = document.querySelector('.product-form__error-message-wrapper');
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
    if(this.errorWrapper) this.errorWrapper.hidden = true;
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === 'minus'
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        'disabled',
        parseInt(value) <= parseInt(this.input.min)
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
    },
  };
}

function fetchGraphQLConfig() {
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Shopify-Storefront-Access-Token': window.graphQL.token,
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options['hideElement'] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    'change',
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.overlay = this.querySelector('.drawer__overlay');
    this.drawer = this.querySelector('.drawer');

    this.close = this.querySelectorAll('.drawer__close');

    this.addEventListener('keyup', this.onKeyUp.bind(this));

    if (this.summary) {
      this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    }

    if (this.close) {
      this.close.forEach((close) => {
        close.addEventListener('click', this.closeMenuDrawer.bind(this));
      });
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', this.closeMenuDrawer.bind(this));
    }
  }

  onSummaryClick() {
    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.closeMenuDrawer(true);
    } else {
      this.openMenuDrawer();
    }

    if (window.matchMedia('(max-width: 990px)')) {
      document.documentElement.style.setProperty(
        '--viewport-height',
        `${window.innerHeight}px`
      );
    }
  }

  openMenuDrawer(forceOpen = false) {
    if (this.mainDetailsToggle) {
      setTimeout(() => {
        this.mainDetailsToggle.classList.add('menu-opening');
      });

      if (typeof forceOpen == 'boolean' && forceOpen) {
        this.mainDetailsToggle.setAttribute('open', true);
      }

      this.summary.setAttribute('aria-expanded', true);
      trapFocus(this.mainDetailsToggle, this.summary);
    } else {
      this.drawer.classList.add('active');
      trapFocus(this.drawer, this.drawer.querySelector('.drawer__close'));
    }

    //document.body.classList.add('overflow-hidden');
  }

  closeMenuDrawer(e) {
    if (this.mainDetailsToggle) {
      this.mainDetailsToggle.classList.remove('menu-opening');

      if (typeof e == 'object') {
        this.mainDetailsToggle.removeAttribute('open');
      }

      this.summary.setAttribute('aria-expanded', false);
      removeTrapFocus(this.summary);
    } else {
      this.drawer.classList.remove('active');
      removeTrapFocus(this.drawer);
    }

    document.body.classList.remove('overflow-hidden');
  }

  onKeyUp(event) {
    if (event.code == 'Escape') {
      this.closeMenuDrawer(event);
    }
  }
}

customElements.define('menu-drawer', MenuDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();

    this.removeOnClose = this.dataset.removeOnClose === 'true';

    this.querySelector('[id^="ModalClose-"]').addEventListener(
      'click',
      this.hide.bind(this, false)
    );
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (
          event.pointerType === 'mouse' &&
          !event.target.closest('deferred-media, product-model')
        )
          this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();

    const media = this.querySelector('deferred-media');

    if (media) {
      media.loadContent();
    }
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();

    if (this.removeOnClose) {
      this.remove();
    }
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              'text/html'
            );
            const sourceQty = html.querySelector(
              '.quick-order-list-container'
            ).parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`
      )
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    const autoplay = this.dataset.autoplay === 'true';

    if (poster) {
      poster.addEventListener('click', this.loadContent.bind(this));
    }

    if (autoplay) {
      this.loadContent(false);
    }
  }

  loadContent(focus = true) {
    //window.pauseAllMedia();

    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(
        this.querySelector('template').content.firstElementChild.cloneNode(true)
      );

      this.setAttribute('loaded', true);

      const deferredElement = content.querySelector(
        'video, model-viewer, iframe'
      );

      if (this.dataset.replace === 'true') {
        // replace the element with the content
        this.replaceWith(deferredElement);
      } else {
        // append the content to the element
        this.appendChild(deferredElement);
      }

      if (focus) deferredElement.focus();
      if (
        deferredElement.nodeName == 'VIDEO' &&
        deferredElement.getAttribute('autoplay')
      ) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');

    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.buttons = this.querySelector('.slider-buttons');
    this.prevButton = this.querySelector('button[name="prev"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.nextButtonVertical = this.querySelector('button[name="nextVertical"]');

    this.dots = this.querySelector('.slider-counter--dots');

    if (!this.slider || (!this.nextButton && !this.nextButtonVertical)) return;

    this.initPages();

    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));

    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    }

    if (this.nextButtonVertical) {
      this.nextButtonVertical.addEventListener(
        'click',
        this.onButtonClick.bind(this)
      );
    }
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );

    if (this.sliderItemsToShow.length < 2) return;

    if (this.nextButton) {
      this.sliderItemOffset =
        this.sliderItemsToShow[1].offsetLeft -
        this.sliderItemsToShow[0].offsetLeft;

      this.slidesPerPage = Math.floor(
        (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
          this.sliderItemOffset
      );

      this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;

      var modifier = 2;

      if (this.nodeName === 'SLIDESHOW-COMPONENT') {
        modifier = 1;
      }

      if (this.totalPages <= modifier && this.sliderItemOffset != 0) {
        this.buttons.classList.add('hidden');
      } else {
        this.buttons.classList.remove('hidden');
      }
    }

    if (this.nextButtonVertical) {
      this.sliderItemOffsetVertical =
        this.sliderItemsToShow[1].offsetTop -
        this.sliderItemsToShow[0].offsetTop;

      this.slidesPerPageVertical = Math.floor(
        (this.slider.clientHeight - this.sliderItemsToShow[0].offsetTop) /
          this.sliderItemOffsetVertical
      );

      this.totalPagesVertical =
        this.sliderItemsToShow.length - this.slidesPerPageVertical + 1;

      if (this.totalPagesVertical <= 1 && this.sliderItemOffsetVertical != 0) {
        this.nextButtonVertical.classList.add('hidden');
      } else {
        this.nextButtonVertical.classList.remove('hidden');
      }
    }

    if (this.dots) {
      this.generateDots();
    }

    this.update();
  }

  generateDots() {
    this.dots.innerHTML = '';

    var modifier = 1;

    if (this.nodeName === 'SLIDESHOW-COMPONENT') {
      modifier = 0;
    }

    for (let i = 0; i < this.totalPages - modifier; i++) {
      const dot = document.createElement('button');

      dot.classList.add('slider-counter__link');
      dot.classList.add('slider-counter__link--dots');
      dot.classList.add('link');

      dot.setAttribute('data-slide-to', i + 1);
      dot.setAttribute('aria-label', `Slide ${i + 1}`);
      dot.setAttribute('aria-controls', this.slider.id);

      dot.addEventListener('click', (event) => {
        const slideTo = event.currentTarget.getAttribute('data-slide-to');
        this.setSlidePosition(this.sliderItemsToShow[slideTo - 1].offsetLeft);
      });

      const span = document.createElement('span');
      span.classList.add('dot');

      dot.appendChild(span);

      this.dots.appendChild(dot);
    }
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1]
      )
    ) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }

    if (this.dots) {
      this.dots.querySelectorAll('.slider-counter__link').forEach((dot) => {
        dot.classList.remove('slider-counter__link--active');
      });

      var dot = this.dots.querySelector(
        `[data-slide-to="${this.currentPage}"]`
      );

      if (dot) {
        dot.classList.add('slider-counter__link--active');
      }
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();

    const step = event.currentTarget.dataset.step || 1;

    if (
      event.currentTarget.name == 'next' ||
      event.currentTarget.name == 'prev'
    ) {
      this.slideScrollPosition =
        event.currentTarget.name === 'next'
          ? this.slider.scrollLeft + step * this.sliderItemOffset
          : this.slider.scrollLeft - step * this.sliderItemOffset;
      this.setSlidePosition(this.slideScrollPosition);
    } else if (
      event.currentTarget.name == 'nextVertical' ||
      event.currentTarget.name == 'prevVertical'
    ) {
      this.slideScrollPosition =
        event.currentTarget.name === 'nextVertical'
          ? this.slider.scrollTop + step * this.sliderItemOffsetVertical
          : this.slider.scrollTop - step * this.sliderItemOffsetVertical;

      this.setSlidePosition(this.slideScrollPosition);
    }
  }

  setSlidePosition(position) {
    if (this.nextButton) {
      this.slider.scrollTo({
        left: position,
      });
    }

    if (this.nextButtonVertical) {
      this.slider.scrollTo({
        top: position,
      });
    }
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll('.slider-counter__link')
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener('click', this.linkToSlide.bind(this))
    );
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.slider.getAttribute('data-autoplay') === 'true')
      this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener(
        'click',
        this.autoPlayToggle.bind(this)
      );
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add(
      'slider-counter__link--active'
    );
    this.sliderControlButtons[this.currentPage - 1].setAttribute(
      'aria-current',
      true
    );
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed
    );
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute(
        'aria-label',
        window.accessibilityStrings.playSlideshow
      );
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute(
        'aria-label',
        window.accessibilityStrings.pauseSlideshow
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  linkToSlide(event) {
    event.preventDefault();

    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) +
          1 -
          this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();

    this.sort = window.variants.sortOptions;

    this.pillsContainer = this.querySelector('.product-form__pills');
    this.pills = this.querySelectorAll('.product-form__pill');

    //this.sort();
  }

  sort() {
    this.pills = Array.from(this.pills).sort((a, b) => {
      const aIndex = this.sort.indexOf(a.dataset.title);
      const bIndex = this.sort.indexOf(b.dataset.title);
      return aIndex - bIndex;
    });

    this.pills.reverse().forEach((pill) => {
      if (pill) {
        this.pillsContainer.insertBefore(pill, this.pillsContainer.firstChild);
      }
    });

    this.pillsContainer.setAttribute('data-loaded', true);
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      // Prevent the OOS form from triggering the variant change event
      if (event.target.type === 'text') {
        event.preventDefault();
        return true;
      } else {
        const target = this.getInputForEventTarget(event.target);
        this.updateSelectionMetadata(event);

        publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            event,
            target,
            selectedOptionValues: this.selectedOptionValues,
          },
        });
      }
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked')
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations();
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations() {
    fetch(`${this.dataset.url}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }

        if (yotpoWidgetsContainer) {
          setTimeout(() => {
            yotpoWidgetsContainer.initWidgets();
          }, 1000);
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener(
      'storefront:signincompleted',
      this.handleStorefrontSignInCompleted.bind(this)
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter(
      (queueElement) => !queue.includes(queueElement)
    );
    const quickBulkElement =
      this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace(
          '[min]',
          event.target.dataset.min
        )
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace(
          '[max]',
          event.target.max
        )
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace(
          '[step]',
          event.target.step
        )
      );
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class QuickviewTrigger extends HTMLElement {
  constructor() {
    super();

    this.productHandle = this.dataset.productHandle;
    this.productUrl = this.dataset.productUrl;
    this.productSku = this.dataset.productSku;

    this.button = this.querySelector('button');

    this.button.addEventListener('click', this.openQuickview.bind(this));
  }

  openQuickview(e) {
    e.preventDefault();

    const quickviewUrl =
      this.productUrl.indexOf('?') > 1
        ? `${this.productUrl}&section_id=quickview`
        : `${this.productUrl}?section_id=quickview`;

    fetch(quickviewUrl)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const quickviewModal = html.querySelector('#PopupModal-Quickview');

        if (quickviewModal) {
          document.body.appendChild(quickviewModal);
          quickviewModal.show(e.target);
          autoAppliedPromo();
          autoApplySkuPromo();
          resolveDiscount();
          resolveColor(hasCodeApplied);
          initRefOnly(this.productSku, '2');
        }
      });
  }
}

if (!customElements.get('quickview-trigger')) {
  customElements.define('quickview-trigger', QuickviewTrigger);
}

class QuickviewContainer extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.actions = this.querySelector('.quickview__actions');
    this.images = this.querySelectorAll('.quickview__image');

    this.form.addEventListener('change', this.handleChange.bind(this));
  }

  handleChange(e) {
    const input = e.target;

    const mediaId = input.dataset.mediaId;

    if (mediaId) {
      let $i = 1;
      this.images.forEach((image) => {
        if (image.dataset.mediaId == mediaId && $i == 1) {
          image.classList.remove('hidden');
          $i++;
        } else {
          image.classList.add('hidden');
        }
      });
    }

    this.actions.classList.remove('hidden');
  }
}

customElements.define('quickview-container', QuickviewContainer);

class WishlistButton extends HTMLElement {
  constructor() {
    super();

    this.productId = this.dataset.productId;
    this.enabled = this.dataset.enabled === 'true';
    this.active = this.dataset.active === 'true';
    this.move = this.dataset.move === 'true';
    this.index = this.dataset.index;

    this.button = this.querySelector('button');

    if (this.move) {
      this.active = false;
    }

    if (this.enabled) {
      this.active = window.customer.wishlist.some(
        (item) => item.id == this.productId
      );

      this.button.addEventListener('click', this.toggleWishlist.bind(this));
    } else {
      this.button.addEventListener('click', this.fetchLoginModal.bind(this));
    }

    this.setAttributes();
  }

  setAttributes() {
    if (this.active) {
      this.setAttribute('data-active', true);
    } else {
      this.setAttribute('data-active', false);
    }

    this.setAttribute('data-loaded', true);
  }

  toggleWishlist(e) {
    e.preventDefault();

    if (!this.enabled) {
      return;
    }

    if (this.active) {
      this.removeFromWishlist();
    } else {
      this.addToWishlist();
    }
  }

  fetchLoginModal() {
    const loginModal = document.querySelector('#WishlistLogin');

    if (loginModal) {
      loginModal.openMenuDrawer(this.button);
    } else {
      fetch(`${location.href}?section_id=wishlist_login`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            'text/html'
          );
          const modal = html.querySelector('#WishlistLogin');

          if (modal) {
            document.body.appendChild(modal);
            modal.openMenuDrawer(this.button);
          }
        });
    }
  }

  addToWishlist() {
    const body = JSON.stringify({
      customer_id: window.customer.id,
      product_id: this.productId,
      action: 'add',
    });

    fetch(`${window.wishlist.endpoint}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((response) => {
        this.active = true;
        this.setAttributes();

        if (this.move) {
          const cartItems =
            this.closest('cart-items') || this.closest('cart-drawer-items');
          cartItems.updateQuantity(this.index, 0);
        }
      });
  }

  removeFromWishlist() {
    const body = JSON.stringify({
      customer_id: window.customer.id,
      product_id: this.productId,
      action: 'remove',
    });

    fetch(`${window.wishlist.endpoint}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((response) => {
        this.active = false;
        this.setAttributes();
      });
  }
}

customElements.define('wishlist-button', WishlistButton);

/* class ChatToggle extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', this.toggleChat.bind(this));
  }

  toggleChat(e) {
    e.preventDefault();
    if (typeof MPChat !== "undefined") {
      MPChat.toggleDisplay(1);
    }
  }
}

customElements.define('chat-toggle', ChatToggle); */

class Pro1clkBase extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', async (event) => {

          let url  = this.dataset.url,
              code = this.dataset.code;

          const formData = { 
            attributes: { auto_apply_shoplift: code } 
          };

          const response = await fetch('/cart/update.js', { 
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }, 
            body: JSON.stringify(formData) 
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const cart = await response.json();
          sessionStorage.setItem('auto_apply_shoplift', code);
          window.location.href = url;

    });
    
  }
}

class Pro1clkHeader extends Pro1clkBase {}
class Pro1clkProduct extends Pro1clkBase {}

customElements.define('pro1clk-header', Pro1clkHeader);
customElements.define('pro1clk-product', Pro1clkProduct);


if(!sessionStorage.getItem('_utm_source')) {
  let urlString = window.location.search,
      urlParams = new URLSearchParams(urlString),
      checkNetwork = urlParams.get('utm_source') || false;
            
  if(checkNetwork) sessionStorage.setItem('_utm_source', checkNetwork);
}

window.addEventListener('load', () => {

  const elements = document.querySelectorAll('.jdgm-arrow');
  elements.forEach(element => {
    element.setAttribute('aria-label', 'Button');
  });

  const heroElements = document.querySelectorAll('.lander [ln1], .lander [ln2], .lander [ln3], .lander [ln4], .lander [ln4], .lander [line1], .lander [line2]');

  if(heroElements) {
    heroElements.forEach(element => {
      let contentValue = window.getComputedStyle(element, '::before').getPropertyValue('content');
      let cleanValue = contentValue.replace(/^["'](.*)["']$/, '$1');
      let accessibilityDiv = element.querySelector('.no--color');
      if(cleanValue != 'none') {
        element.setAttribute('alt', cleanValue)
        accessibilityDiv.textContent = cleanValue;
      }
    });
  }

});